import * as RadixDialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from './Button'
import { cn } from '@/lib/utils'

export interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  /** `full` is used by the record detail and Compare views, which are near full-screen. */
  size?: 'sm' | 'md' | 'lg' | 'full'
  hideClose?: boolean
}

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  full: 'max-w-[min(1200px,calc(100vw-3rem))]',
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  hideClose,
}: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-ink/35 backdrop-blur-[2px]" />
        <RadixDialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
            'max-h-[calc(100vh-3rem)] overflow-hidden rounded-[var(--radius-card)] border border-line',
            'bg-paper shadow-[var(--shadow-float)] flex flex-col',
            SIZES[size],
          )}
        >
          {(title || !hideClose) && (
            <header className="flex items-start justify-between gap-4 px-6 pt-6">
              <div>
                {title && (
                  <RadixDialog.Title className="font-display text-xl font-semibold text-ink">
                    {title}
                  </RadixDialog.Title>
                )}
                {description && (
                  <RadixDialog.Description className="mt-1 text-sm text-ink-muted">
                    {description}
                  </RadixDialog.Description>
                )}
              </div>
              {!hideClose && (
                <RadixDialog.Close className="rounded-md p-1 text-ink-muted transition-colors hover:bg-paper-muted hover:text-ink">
                  <X className="size-5" />
                  <span className="sr-only">Close</span>
                </RadixDialog.Close>
              )}
            </header>
          )}

          <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

          {footer && (
            <footer className="flex items-center justify-end gap-2 border-t border-line px-6 py-4">
              {footer}
            </footer>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}

export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: ReactNode
  confirmLabel: string
  cancelLabel?: string
  onConfirm: () => void
  loading?: boolean
  children?: ReactNode
}

/** A destructive action behind one deliberate click. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  loading,
  children,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button variant="danger" loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Dialog>
  )
}
